// The module's one player for any item (spec 2026-10-24 link kinds). It picks the surface from the item's kind —
// the hosted embed, the audio / video element, the photo — and owns what every player shares: the event stream, the
// control handle, "Tap to listen" and "Can't play here". A screen passes the item, its handlers and its layout; it
// never chooses a player.
import type { ReactNode, Ref } from "react";
import CantPlayHere from "./CantPlayHere";
import EmbedSurface from "./surfaces/EmbedSurface";
import ElementSurface from "./surfaces/ElementSurface";
import PhotoSurface from "./surfaces/PhotoSurface";
import { playerKey, resolveItem, type ResolvedItem } from "./registry";
import type { MediaEvent, MediaFrame, MediaHandle } from "./media";
import type { PlayableItem, PlayerKind, Surface } from "./types";

export interface PlayableMediaProps {
  item: PlayableItem;
  /** The kind and canPlay the screen already resolved (resolveItem), so it is not asked twice. */
  resolved?: ResolvedItem;
  onEvent?(e: MediaEvent): void;
  /** Play / pause / seek / rate / reload / sample (a cast follower drives this). */
  handleRef?: Ref<MediaHandle>;
  /** Start sounding as soon as the media is loaded (asked at each load). Default: wait for play(). */
  autoPlay?: boolean | (() => boolean);
  /** Where a load starts (asked at each load). Default 0. */
  startMs?: () => number;
  /** The screen wants sound now: a loaded player that stays silent is offered "Tap to listen". */
  shouldPlay?: boolean;
  /**
   * Elements: a silent start is a block too (play() refusing is always one). Embeds report no refusal, so for them a
   * silent start always is.
   */
  silenceIsBlocked?: boolean;
  /** Restarts the silent-start wait (new media, a new timeline). */
  blockedKey?: unknown;
  /** Waited on top of the silent-start wait (a cast's scheduled start). */
  blockedDelayMs?: () => number;
  /** Replaces the tap's default play() (a cast resyncs to its timeline). */
  onTapToListen?(): void;
  /** Draw "Tap to listen" elsewhere (the full-screen band): handed the tap while blocked, else null. */
  tapToListenOut?(tap: (() => void) | null): void;
  /** Wrapper class of a "Tap to listen" drawn under the player (default "px-2 pt-2"). */
  tapToListenClassName?: string;
  /** The provider's own controls where the kind has them (`caps.ownControls`) and the hosted player offers them. */
  providerControls?: boolean;
  /** Draw "Can't play this link" over the frame on an error. */
  showError?: boolean;
  /** The screen's box around a picture (and an audio face). */
  frame?: MediaFrame;
  /** Audio: draw its face (glyph and name) in the frame; without it audio has no surface. */
  face?: boolean;
  /** The media element / hosted-player host. */
  hostTestId?: string;
  hostClassName?: string;
  /** Replaces the default "Can't play here" card (null: nothing). */
  cantPlay?: ReactNode;
}

/** What each surface gets: the props, its kind and the URL it plays. */
export interface SurfaceProps extends PlayableMediaProps {
  kind: PlayerKind;
  url: string;
}

const SURFACES: Record<Surface, (p: SurfaceProps) => ReactNode> = {
  embed: EmbedSurface,
  audio: ElementSurface,
  video: ElementSurface,
  photo: PhotoSurface,
};

export default function PlayableMedia(props: PlayableMediaProps) {
  const { kind, playable } = props.resolved ?? resolveItem(props.item);
  if (!playable) return <>{props.cantPlay !== undefined ? props.cantPlay : <CantPlayHere item={props.item} />}</>;
  const url = kind.playUrl(props.item);
  if (!url) return null;
  const Player = SURFACES[kind.surface];
  // A new player when the rules change (another surface, live ↔ timed); the same one across items otherwise.
  return <Player key={playerKey(kind)} {...props} kind={kind} url={url} />;
}
